import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert } from 'antd';
import { ArtifactService } from '../../services/api/artifactService';
import { BusinessModelService } from '../../services/api/businessModelService';
import { ArtifactTree } from '../../types/artifacts';
import { useArtifactTree } from '../../contexts/ArtifactTreeContext';
import { useAppDispatch } from '../../store/hooks';
import { setCurrentModel } from '../../store/slices/BusinessModelSlice';
import { checkRootStatus } from '../../store/slices/ArtifactSlice';
import { useUser } from '../../contexts/UserContext';
import { canUsePrimeArtifactFeatures } from '../AppShell/appNavAccess';
import { useAppNavAccess } from '../AppShell/useAppNavAccess';
import { useCompanyArtifactsSlugSession } from '../../hooks/useCompanyArtifactsSlugSession';
import {
  fetchCompanyArtifactModelRoots,
  type CompanyArtifactModelRoot,
} from '../../utils/companyArtifactModelRoots';
import { companyArtifactsModelPath } from '../../utils/companyArtifactRoutes';
import { companySelectionAlertDescription } from '../../utils/companySelectionHelp';
import { resolveTreeForModelSlug } from '../../utils/resolveTreeForModelSlug';
import { resolveCompanyForModelSlug } from '../../utils/resolveCompanyIdForModelSlug';
import styles from './ArtifactTreeSelection.module.css';

export const ArtifactTreeSelection: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { setSelectedTree } = useArtifactTree();
  const { selectedCompany, availableCompanies } = useUser();
  const navAccess = useAppNavAccess();
  const canCreatePrimeTree = canUsePrimeArtifactFeatures(navAccess);
  const { effectiveSlug, derivedSlug, requiresCompanyContext } = useCompanyArtifactsSlugSession();
  const [modelRoots, setModelRoots] = useState<CompanyArtifactModelRoot[]>([]);
  const [dbTrees, setDbTrees] = useState<ArtifactTree[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newTreeName, setNewTreeName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!effectiveSlug) {
        setModelRoots([]);
        setDbTrees([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const accessibleCompanyIds = availableCompanies.map((uc) => uc.company.id);
        const [roots, trees] = await Promise.all([
          fetchCompanyArtifactModelRoots(effectiveSlug),
          ArtifactService.getTrees(selectedCompany?.id, accessibleCompanyIds),
        ]);
        if (cancelled) return;
        setModelRoots(roots);
        setDbTrees(trees);
      } catch (err) {
        if (cancelled) return;
        setError('Failed to load company models');
        console.error(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [effectiveSlug, selectedCompany?.id, availableCompanies]);

  const handleModelSelect = async (root: CompanyArtifactModelRoot) => {
    navigate(companyArtifactsModelPath(root.slug));

    const { companyId } = resolveCompanyForModelSlug(
      root.slug,
      selectedCompany,
      availableCompanies
    );
    const accessibleCompanyIds = availableCompanies.map((uc) => uc.company.id);
    let tree = resolveTreeForModelSlug(dbTrees, root.slug, { companyId: companyId ?? undefined });
    if (!tree && companyId) {
      try {
        const scopedTrees = await ArtifactService.getTrees(companyId, accessibleCompanyIds);
        tree = resolveTreeForModelSlug(scopedTrees, root.slug, { companyId });
      } catch (err) {
        console.warn('[ArtifactTreeSelection] Failed to load scoped trees:', err);
      }
    }

    if (!tree) {
      setSelectedTree(null);
      return;
    }

    setSelectedTree(tree);
    void (async () => {
      try {
        await dispatch(checkRootStatus({ treeId: tree!.id, companyId })).unwrap();
      } catch (err) {
        console.warn('[ArtifactTreeSelection] Root status check failed (continuing):', err);
      }

      try {
        const activeModel = await BusinessModelService.getActiveByTreeId(tree!.id);
        if (activeModel) {
          await dispatch(setCurrentModel(activeModel));
        }
      } catch (modelError: unknown) {
        console.warn('[ArtifactTreeSelection] Could not load active business model:', modelError);
      }
    })();
  };

  const [linkModelSlug, setLinkModelSlug] = useState('');

  const handleCreateTree = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTreeName.trim()) return;

    try {
      setIsCreating(true);
      const slugForMetadata = linkModelSlug.trim() || effectiveSlug || '';
      const { companyId } = resolveCompanyForModelSlug(
        slugForMetadata || undefined,
        selectedCompany,
        availableCompanies
      );
      const metadata: Record<string, unknown> = {};
      if (companyId) {
        metadata.company_id = companyId;
      }
      if (slugForMetadata) {
        metadata.kryspace_slug = slugForMetadata;
      }
      const newTree = await ArtifactService.createTree(newTreeName.trim(), undefined, metadata);
      setDbTrees((prev) => [...prev, newTree]);
      setNewTreeName('');
    } catch (err) {
      setError('Failed to create tree');
      console.error(err);
    } finally {
      setIsCreating(false);
    }
  };

  if (requiresCompanyContext && !effectiveSlug) {
    return (
      <div className={styles.artifactTreeSelection}>
        <Alert
          type="info"
          showIcon
          message="Select a company"
          description={companySelectionAlertDescription({ includeCreateHint: true })}
        />
      </div>
    );
  }

  if (!effectiveSlug) {
    return (
      <div className={styles.artifactTreeSelection}>
        <Alert
          type="warning"
          showIcon
          message="Workspace slug unavailable"
          description="Could not resolve a kryspace workspace slug for the selected company."
        />
      </div>
    );
  }

  if (loading && modelRoots.length === 0) {
    return <div className={styles.artifactTreeSelection}>Loading company models...</div>;
  }

  if (error && modelRoots.length === 0) {
    return <div className={`${styles.artifactTreeSelection} ${styles.error}`}>{error}</div>;
  }

  const slugSourceLabel =
    derivedSlug?.source === 'kryspace_slug'
      ? 'kryspace_slug'
      : derivedSlug?.source === 'metadata'
        ? 'company metadata'
        : derivedSlug?.source === 'name_hint'
          ? 'name hint'
          : 'session';

  return (
    <div className={styles.artifactTreeSelection}>
      <h2>Select a company model</h2>
      <p className={styles.lede}>
        Models are root artifacts in the <code>artifact-tree</code> for workspace{' '}
        <strong>{effectiveSlug}</strong>
        {derivedSlug ? <> (resolved via {slugSourceLabel})</> : null}.
      </p>

      {error ? <div className={styles.errorBanner}>{error}</div> : null}

      <div className={styles.treesList}>
        {modelRoots.map((root) => (
          <button
            key={root.slug}
            type="button"
            className={styles.treeItem}
            onClick={() => void handleModelSelect(root)}
            disabled={loading}
          >
            <h3>{root.title}</h3>
            <p className={styles.slugLine}>{root.slug}</p>
            {root.statusPhrase ? <p className={styles.metaLine}>{root.statusPhrase}</p> : null}
          </button>
        ))}
      </div>

      <details className={styles.legacySection}>
        <summary>Legacy artifact tree (database)</summary>
        {!canCreatePrimeTree ? (
          <Alert
            type="info"
            showIcon
            message="Artifact tree creation requires a subscription"
            description="Your plan does not include prime artifact modeling. Contact your administrator or upgrade to create company artifact trees."
            style={{ marginBottom: 12 }}
          />
        ) : null}
        <form onSubmit={handleCreateTree} className={styles.createTreeSection}>
          <input
            type="text"
            value={newTreeName}
            onChange={(e) => setNewTreeName(e.target.value)}
            placeholder="Enter tree name"
            disabled={isCreating || !canCreatePrimeTree}
          />
          <input
            type="text"
            value={linkModelSlug}
            onChange={(e) => setLinkModelSlug(e.target.value)}
            placeholder="Link kryspace slug (e.g. prime_ops)"
            disabled={isCreating || !canCreatePrimeTree}
          />
          <button type="submit" disabled={isCreating || !newTreeName.trim() || !canCreatePrimeTree}>
            {isCreating ? 'Creating...' : 'Create tree'}
          </button>
        </form>
        {dbTrees.length > 0 ? (
          <ul className={styles.legacyList}>
            {dbTrees.map((tree) => (
              <li key={tree.id}>{tree.name}</li>
            ))}
          </ul>
        ) : (
          <p className={styles.metaLine}>No database artifact trees for this company yet.</p>
        )}
      </details>
    </div>
  );
};
